import React from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { SimpleTextInput } from "components/FormTextInput";
import { SimpleSelectInput } from "components/FormSelectInput";
import { Save, HelpOutline } from "@mui/icons-material";
import sessionHelperInstance from "services/sessionHelper";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
  },
  sectionTitle: {
    fontWeight: "600",
    color: "#333333",
    marginBottom: theme.spacing(2),
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: theme.spacing(1),
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: theme.spacing(2),
  },
  inputField: {
    width: "100%",
  },
  sectionSaveButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: "bold",
    "&:hover": {
      backgroundColor: "#45a049",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
}));

const OperatorInformation = ({
  operatorDetails,
  onChange,
  onSave,
  loading,
}: any) => {
  const styles = useStyles();

  const getCountryOptions = () => {
    const data = sessionHelperInstance.countries.map((country) => ({
      id: country?.isoCode,
      label: country?.name,
    }));

    return [{ id: null, label: "Blank" }, ...data];
  };

  return (
    <Paper elevation={1} className={styles.paperContainer}>
      <Typography variant="h6" className={styles.sectionTitle}>
        Operator Information & Settings
      </Typography>

      <Box className={styles.gridContainer}>
        <Box>
          <SimpleTextInput
            label="Operator Name"
            name="operatorName"
            value={operatorDetails.operatorName}
            onChange={onChange}
            className={styles.inputField}
          />
        </Box>

        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <SimpleTextInput
              label="External ID (Casino ID)"
              name="externalOpId"
              value={operatorDetails.externalOpId || ""}
              onChange={onChange}
              className={styles.inputField}
              placeholder="Enter external casino identifier"
            />
            <Tooltip
              title="External ID is an optional unique identifier used by third-party casino systems to reference this operator. This ID helps in mapping and synchronizing data between different gaming platforms and our system. It can be any alphanumeric string that uniquely identifies the operator in external systems."
              placement="top"
              arrow
            >
              <IconButton size="small" sx={{ mt: 2 }}>
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box>
          <SimpleSelectInput
            readonlyvalue={
              sessionHelperInstance.platformOptions?.find(
                (el) => el.id === operatorDetails.platform
              )?.label
            }
            label="Platform"
            name="platform"
            value={operatorDetails.platform}
            options={sessionHelperInstance.platformOptions}
            onChange={onChange}
            className={styles.inputField}
          />
        </Box>

        <Box>
          <SimpleSelectInput
            label="Country"
            name="countryCode"
            value={operatorDetails?.countryCode || ""}
            options={getCountryOptions()}
            onChange={onChange}
            className={styles.inputField}
          />
        </Box>

        <Box>
          <SimpleSelectInput
            name="languageCode"
            label="Select Language"
            onChange={onChange}
            options={[
              {
                id: null,
                label: "Blank",
              },
              ...(sessionHelperInstance.languageOptions ?? []),
            ]}
            value={operatorDetails.languageCode || ""}
            className={styles.inputField}
          />
        </Box>
      </Box>

      {/* Section Save Button */}
      {onSave && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
            startIcon={<Save />}
            className={styles.sectionSaveButton}
          >
            {loading ? "Saving..." : "Save Information"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default OperatorInformation;
